import { useState, useEffect } from 'react'
import { useCreateFolder } from '../../hooks/useFolders'

interface CreateFolderModalProps {
  onClose: () => void
  onSuccess: () => void
}

const FOLDER_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280'  // Gray
]

export function CreateFolderModal({ onClose, onSuccess }: CreateFolderModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedColor, setSelectedColor] = useState('#3B82F6')
  const createFolder = useCreateFolder()

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!name.trim()) {
      alert('フォルダ名を入力してください')
      return
    }

    try {
      console.log('📁 Creating folder:', { name: name.trim(), color: selectedColor })
      await createFolder.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        color: selectedColor
      })
      console.log('✅ Folder created successfully')
      
      // すぐにコールバックを呼び出す（親コンポーネントでrefetchする）
      onSuccess()
    } catch (error) {
      console.error('❌ Folder creation failed:', error)
      alert(`フォルダの作成に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-card"
        style={{ width: 448 }}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-header">
            <h2 className="text-lg font-semibold">
              新しいフォルダを作成
            </h2>
            <button type="button" className="close-btn" onClick={onClose} title="閉じる">
              <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="modal-body">
            {/* フォルダ名 */}
            <div className="form-group">
              <label className="form-label">
                フォルダ名 <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
                placeholder="フォルダ名を入力"
                maxLength={50}
                required
              />
            </div>

            {/* 説明 */}
            <div className="form-group">
              <label className="form-label">
                説明（任意）
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-input"
                placeholder="フォルダの説明を入力"
                rows={3}
                maxLength={200}
              />
            </div>

            {/* カラー選択 */}
            <div className="form-group">
              <label className="form-label">
                フォルダの色
              </label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setSelectedColor(color)}
                    className="w-8 h-8 rounded-full transition-all"
                    style={{
                      backgroundColor: color,
                      border: selectedColor === color ? '2px solid var(--color-text-primary)' : '2px solid var(--color-border-subtle)',
                      transform: selectedColor === color ? 'scale(1.1)' : undefined
                    }}
                    title={`色: ${color}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ボタン */}
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={createFolder.isPending || !name.trim()}
              className="btn btn-primary"
              data-state={createFolder.isPending || !name.trim() ? 'disabled' : undefined}
            >
              {createFolder.isPending ? '作成中...' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
